import React, { ReactNode, useEffect, useMemo } from "react";
import { RemoteCard, Traveler } from "types";
import { ManageActionButton } from "../Dashboard/Roster/ManageActionButton";
import { SavedCardMenu } from "./SavedCardMenu";
import { useVisibility } from "@/hooks/useVisibility";

interface TravelerCardListProps {
    organizer?: Traveler;
    value: RemoteCard | null;
    isPartnerBooking?: boolean;
    children?: ReactNode;
    onChange: (card: RemoteCard | null) => void;
}

export const TravelerCardList = ({ organizer, value, isPartnerBooking, children, onChange }: TravelerCardListProps) => {
    const savedCards = useMemo(() => organizer?.creditCards ?? [], [organizer?.creditCards]);
    const [addingNewCard, { set: setAddingNewCard }] = useVisibility(false);

    useEffect(() => {
        // If its a partner booking, we don't want to update the state
        if (isPartnerBooking) {
            return;
        }

        // Run onChange handler to trigger the state update as it can be overwritten (lineItem) with default value while we already have a card selected
        if (value) {
            onChange(value);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isPartnerBooking) {
            return;
        }

        const [firstCard] = savedCards;
        setAddingNewCard(false);
        if (!value && firstCard && savedCards.length === 1) {
            onChange(firstCard);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedCards]);

    const handleChangeSelected = (e: any, name: string, id: ID | null) => {
        const matchingItem = id
            ? savedCards?.find((savedCard) => savedCard.id === id)
            : savedCards.find((savedCard) => new RegExp(savedCard.last4).exec(name));

        if (matchingItem) {
            onChange(matchingItem);
        }
    };

    if (!organizer || isPartnerBooking) {
        return <div>{children}</div>;
    }

    if (value || (savedCards.length > 1 && !addingNewCard)) {
        return (
            <SavedCardList
                savedCards={savedCards}
                selectedSavedCard={value}
                onChangeSelected={handleChangeSelected}
                onAddNewCard={() => {
                    setAddingNewCard(true);
                    onChange(null);
                }}
            />
        );
    }

    return (
        <div className="flex">
            {children}

            {!organizer && savedCards.length > 0 ? (
                <SavedCardMenu
                    savedCards={savedCards}
                    selectedSavedCard={value}
                    expanded={false}
                    onChangeSelected={handleChangeSelected}
                />
            ) : null}
        </div>
    );
};

interface SelectedCardDisplayProps {
    savedCards: RemoteCard[];
    selectedSavedCard: RemoteCard | null;
    onChangeSelected: (e: any, name: string, id: ID | null) => void;
    onAddNewCard: () => void;
}

const SavedCardList = ({ savedCards, selectedSavedCard, onChangeSelected, onAddNewCard }: SelectedCardDisplayProps) => {
    return (
        <div>
            <SavedCardMenu
                expanded
                savedCards={savedCards}
                selectedSavedCard={selectedSavedCard}
                onChangeSelected={onChangeSelected}
            />

            <ManageActionButton text="Add a new card" onClick={onAddNewCard} />
        </div>
    );
};
